import { ScrollableFooter, ScrollableParent, Step, useComputed } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import { StepperProvider, useStepperContext } from "src/components/stepper";
import { BidPackageStatus, ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PlanBasedBidPackageDetailsStep } from "src/routes/bids/plan-based-bid-package/components/1.PlanBasedBidPackageDetailsStep";
import { ConfigureBidGroupsStep } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep";
import { ReviewAndDownloadStep } from "src/routes/bids/plan-based-bid-package/components/3.ReviewAndDownloadStep";
import { PlanBasedBidPackageStepActions } from "src/routes/bids/plan-based-bid-package/components/PlanBasedBidPackageStepActions";
import {
  PlanBasedBidPackagePageQuery,
  PlanBasedBidPackagePage_BidPackageFragment,
  PlanBasedBidPackagePage_OriginatingProjectFragment,
  usePlanBasedBidPackagePageQuery,
  usePlanBasedBidPackagePage_OriginatingProjectQuery,
} from "src/routes/bids/plan-based-bid-package/PlanBasedBidPackagePage.generated";
import {
  BaseBidType,
  INTERNAL_ESTIMATE_ID,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormObjectState,
  PlanBasedBidPackageFormState,
  PlanBasedBidPackageStep,
  initialSteps,
  isBaseGroup,
  planBasedBidPackageFormConfig,
} from "src/routes/bids/plan-based-bid-package/utils";
import { BidPackageParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";

export function PlanBasedBidPackagePage() {
  const { bidPackageGroupId } = useRouteParams<BidPackageParams>();
  const [{ projectId }] = useQueryParams({ projectId: StringParam });
  const query = usePlanBasedBidPackagePageQuery({
    variables: { id: bidPackageGroupId || "" },
    skip: !bidPackageGroupId,
  });
  const originatingProjectQuery = usePlanBasedBidPackagePage_OriginatingProjectQuery({
    variables: { id: projectId! },
    skip: !!bidPackageGroupId || !projectId,
  });

  if (!bidPackageGroupId) {
    return projectId ? (
      queryResult(originatingProjectQuery, ({ project }) => (
        <PlanBasedBidPackageView isEdit={false} originatingProject={project} />
      ))
    ) : (
      <PlanBasedBidPackageView isEdit={false} />
    );
  }

  return queryResult(query, ({ bidPackageGroup }) => (
    <PlanBasedBidPackageView
      isEdit={bidPackageGroup.status.code !== BidPackageStatus.Draft}
      bidPackageGroup={bidPackageGroup}
    />
  ));
}

type PlanBasedBidPackageViewProps = {
  isEdit: boolean;
  bidPackageGroup?: PlanBasedBidPackagePageQuery["bidPackageGroup"];
  originatingProject?: PlanBasedBidPackagePage_OriginatingProjectFragment;
};

function PlanBasedBidPackageView(props: PlanBasedBidPackageViewProps) {
  const { isEdit, bidPackageGroup, originatingProject } = props;
  const isCreate = !isEdit;
  const formState = useFormState({
    config: planBasedBidPackageFormConfig,
    init: {
      input: bidPackageGroup?.bidPackages.first,
      map: (bidPackage) => mapBidPackageToForm(bidPackage),
      ifUndefined: {
        marketId: originatingProject?.market?.id,
        projects: originatingProject ? [originatingProject.id] : [],
        // If we're coming from a community project, then pre-select all active product offerings
        productOfferings:
          originatingProject && "productOfferings" in originatingProject
            ? originatingProject.productOfferings
                .filter((po) => po.status.code === ReadyPlanStatus.Active)
                .map((po) => po.id)
            : [],
        bidPlanGroups: [{ order: 0, clientId: "base:1", dimensions: [] }],
        baseBidType: BaseBidType.Standard,
      },
    },
  });

  // Warn before discarding unsaved edits when the user clicks a different step in the stepper bar.
  // `nextStep` (the Continue button) overrides this guard, so it only fires on direct step clicks / Back.
  const isDirty = useComputed(() => formState.dirty, [formState]);

  // Derive each step's enabled/complete state straight from the form (MobX). `useComputed` rebuilds the
  // array reactively and `StepperProvider` picks it up, so gating stays declarative — no imperative
  // `setSteps` (which got dropped on the initial render, leaving steps stuck disabled for filled drafts).
  const steps = useComputed<Step[]>(() => {
    const coreReady = !!formState.marketId.value && !!formState.costCodes.value?.nonEmpty && formState.costType.valid;
    const hasBidGroups = (formState.bidPlanGroups.value ?? []).some((g) => !g.archive);
    const [define, configure, review] = initialSteps(isCreate);
    return [
      { ...define, disabled: false, state: coreReady ? "complete" : "incomplete" },
      { ...configure, disabled: !coreReady, state: coreReady && hasBidGroups ? "complete" : "incomplete" },
      { ...review, disabled: !coreReady || !hasBidGroups },
    ];
  }, [formState, isCreate]);

  return (
    <StepperProvider steps={steps} shouldConfirmStepChange={isDirty}>
      <PlanBasedBidPackageContent formState={formState} isEdit={isEdit} originatingProject={originatingProject} />
    </StepperProvider>
  );
}

type PlanBasedBidPackageContentProps = {
  formState: PlanBasedBidPackageFormObjectState;
  isEdit: boolean;
  originatingProject?: PlanBasedBidPackagePage_OriginatingProjectFragment;
};

function PlanBasedBidPackageContent(props: PlanBasedBidPackageContentProps) {
  const { formState, isEdit, originatingProject } = props;
  const { currentStep } = useStepperContext();

  const step = useMemo(() => {
    switch (currentStep.value) {
      case PlanBasedBidPackageStep.DEFINE_PACKAGE:
        return (
          <PlanBasedBidPackageDetailsStep
            formState={formState}
            isEdit={isEdit}
            originatingProject={originatingProject}
          />
        );
      case PlanBasedBidPackageStep.CONFIGURE_BID_GROUPS:
        return <ConfigureBidGroupsStep formState={formState} isEdit={isEdit} />;
      case PlanBasedBidPackageStep.REVIEW_AND_DOWNLOAD:
        return <ReviewAndDownloadStep formState={formState} />;
      default:
        return <PlanBasedBidPackageDetailsStep formState={formState} isEdit={isEdit} />;
    }
  }, [currentStep.value, formState, isEdit, originatingProject]);

  return (
    <ScrollableParent>
      {step}
      <ScrollableFooter>
        <PlanBasedBidPackageStepActions formState={formState} isEdit={isEdit} />
      </ScrollableFooter>
    </ScrollableParent>
  );
}

export function mapBidPackageToForm(
  bidPackage: PlanBasedBidPackagePage_BidPackageFragment,
): PlanBasedBidPackageFormState {
  const internalEstimate = bidPackage.internalEstimateBidContract;
  const hasInternalEstimate = internalEstimate && !internalEstimate.archivedAt;
  const bidPlanGroups = bidPackage.bidPlanGroups.map((bpg) => ({
    id: bpg.id,
    globalOptionId: bpg.globalOption?.id,
    primaryOptionGroupId: bpg.primaryOptionGroup?.id,
    order: bpg.order,
    archive: false,
    dimensions: bpg.dimensions.map((d) => ({
      id: d.id,
      // A dimension is either type-based or group-based (personalization), never both.
      globalOptionTypeId: d.globalOptionType?.id,
      globalOptionGroupId: d.globalOptionGroup?.id,
      globalOptionIds: d.globalOptions.map((go) => go.id),
    })),
  }));
  return {
    bidPackageGroupId: bidPackage.bidPackageGroup?.id,
    bidPackageId: bidPackage.id,
    marketId: bidPackage.projects.first?.market?.id,
    projects: bidPackage.projects.map((p) => p.id),
    costCodes: bidPackage.costCodes.map((cc) => cc.id),
    costType: bidPackage.costType.code,
    internalEstimateType: hasInternalEstimate ? internalEstimate.internalEstimateType?.code : undefined,
    productOfferings: bidPackage.productOfferings.map((po) => po.id),
    requests: [
      ...(hasInternalEstimate ? [{ id: internalEstimate.id, tradePartnerId: INTERNAL_ESTIMATE_ID }] : []),
      ...bidPackage.requests.map((r) => ({ id: r.id, tradePartnerId: r.tradePartner.id, archive: !!r.archivedAt })),
    ],
    bidPlanGroups,
    baseBidType: deriveBaseBidType(bidPlanGroups),
  };
}

/** Derives the base bid mode from the mapped groups. */
function deriveBaseBidType(groups: PlanBasedBidPackageBidPlanGroup[]): BaseBidType {
  const baseGroups = groups.filter(isBaseGroup);
  const hasPersonalization = baseGroups.some(
    (g) => g.primaryOptionGroupId || (g.dimensions ?? []).some((d) => d.globalOptionGroupId),
  );
  if (hasPersonalization) return BaseBidType.Personalization;
  return baseGroups.nonEmpty ? BaseBidType.Standard : BaseBidType.None;
}
